import React, { FC, useMemo } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from '@gatsbyjs/reach-router';
import { ErrorManagement } from '@printdeal/error-management/react';
import { SegmentationData } from '@printdeal/segmentation/react';
import { Section, Spinner } from '@printdeal/ui-library';
import { useStoreToggle } from '@printdeal/feature-management/react';
import { RootState } from '../../../store';
import { ServiceFactory } from '../../../services/ServiceFactory';
import { selectors as authSelectors } from '../../../store/slices/authSlice';
import { PrimaryBannerPanel } from '../banner/PrimaryBanner/PrimaryBannerPanel';
import { FeaturedProducts } from '../product-list/featured-products/FeaturedProducts';
import { ORDERS_IN_SECTION } from '../recent-order/RecentOrderHelper';
import { getSectionData } from '../helpers';
import LayoutSection from '../../layout-section/LayoutSection';
import StaticLoginBox from '../login-box/StaticLoginBox';
import RecentOrder from '../recent-order/RecentOrder';
import GuidedSearchHero from '../guided-search/GuidedSearchHero';
import { AudiencesData, getQueryParamFromLocation } from '../../../templates/homepage/homepage-helper';
import type { HomePageBannerData } from '../banner/types';
import type { ProductProps } from '../product-list/types';
import { HomepageSectionProps } from '../types';
// @ts-expect-error
import { selectors as customerSelectors } from '../../../store/slices/customerSlice';
import ClientOnly from '../../ClientOnly';
import { getFromLocalStorage, LS_KEYS } from '../../../helpers/window';

export interface PrimaryBannerSectionProps {
  section?: HomepageSectionProps;
  audiencesDataFromSegment: AudiencesData;
}

const LoadingSpinner = ({ columnSpan = 3 }) => {
  const className = `md:pd-col-span-${columnSpan}`;
  return (
    <div
      className={
        clsx('pd-hidden pd-min-h-[50rem] md:pd-order-1 md:pd-flex md:pd-items-center md:pd-justify-center', className)
      }
    >
      <Spinner size="lg" />
    </div>
  );
};

export const PrimaryBannerSection: FC<PrimaryBannerSectionProps> = ({
  section,
  audiencesDataFromSegment,
}) => {
  const location = useLocation();
  const isLoggedIn = useSelector(authSelectors.isLoggedIn);
  const isAuthLoading = useSelector(authSelectors.isAuthLoading);
  const customerId = useSelector<RootState, string | undefined>((state) => customerSelectors.getCustomerId(state));
  const audienceOverRideForPlayground = getQueryParamFromLocation(location, 'variant');
  const isImpressto = useStoreToggle('impressto-fr').featureToggle.isEnabled;
  const segmentData = JSON.parse(getFromLocalStorage(LS_KEYS.SEGMENTATION_DATA) || '{}') as SegmentationData;
  const userType = {
    isEnduser: segmentData.isEnduser,
    isReseller: segmentData.isReseller,
  };

  const {
    marketingBanner,
    featuredProducts,
  } = useMemo(
    () => ({
      marketingBanner: getSectionData(section, 'primary_banner') as HomePageBannerData,
      featuredProducts: getSectionData(section, 'featured_product_list') as ProductProps,
    }),
    [section],
  );

  const paramsForQuery = { ordersPerPage: ORDERS_IN_SECTION };
  const queryKeyForRecentOrdersHome = ['recentOrders', { ...paramsForQuery, customerId }];
  const {
    data, isSuccess: isOrdersLoaded, isLoading: isLoadingOrders, error, isError,
  } = useQuery({
    queryKey: queryKeyForRecentOrdersHome,
    queryFn: () => ServiceFactory.createOrderService().getOrders(paramsForQuery),
    enabled: isLoggedIn,
  });

  const orders = data?.results;
  const recentOrderEnabledOnHome = isLoggedIn && orders && isOrdersLoaded && orders.length > 0;
  const showLoginOrRecentOrders = !isLoggedIn || recentOrderEnabledOnHome;
  const showRecentOrders = recentOrderEnabledOnHome && !isLoadingOrders;
  const showLoginBox = !isAuthLoading && !isLoggedIn && !isLoadingOrders;

  if (isError) {
    ErrorManagement.exception({
      exception: error,
      tags: {
        component: 'PrimaryBannerSection',
        fileLocation: 'components-home-HomepageSections',
        featureFunction: 'queryForRecentOrdersHome',
      },
      fingerprint: ['PrimaryBannerSection-queryForRecentOrdersHome-response-results'],
    });
  }

  const primaryBannerPanel = Object.keys(marketingBanner).length !== 0 ? (
    <PrimaryBannerPanel
      bannerData={marketingBanner}
      audiencesDataFromSegment={audiencesDataFromSegment}
      audienceOverRideForPlayground={audienceOverRideForPlayground}
    />
  ) : null;

  const featuredProductsPanel = Object.keys(featuredProducts).length !== 0 && !isLoadingOrders ? (
    <FeaturedProducts
      featuredProducts={featuredProducts}
      showLoginOrRecentOrders={showLoginOrRecentOrders}
      audiencesDataFromSegment={audiencesDataFromSegment}
      audienceOverRideForPlayground={audienceOverRideForPlayground}
    />
  ) : null;

  if (isImpressto) {
    return (
      <Section className="pd-py-0">
        <GuidedSearchHero />
      </Section>
    );
  }
  if (isAuthLoading) {
    return (
      <LoadingSpinner />
    );
  }

  return (
    <Section
      as={LayoutSection}
      variant={section?.backgroundStyle || 'white'}
      withSidebar={false}
      withDivider={false}
      backgroundColor=""
      containerClassName="pd-grid pd-gap-10 lg:pd-grid-cols-3 pd-relative"
      className="pd-min-h-[50rem]"
    >
      <ClientOnly>
        <>
          {featuredProductsPanel}
          {primaryBannerPanel}
        </>
        {showLoginBox && (
          <div className="pd-hidden pd-h-full md:pd-block lg:pd-order-3 lg:pd-col-span-1">
            <StaticLoginBox
              userType={userType}
            />
          </div>

        )}
        {showRecentOrders && (
          <div className="pd-hidden pd-h-full md:pd-block lg:pd-order-3 lg:pd-col-span-1">
            <RecentOrder orders={orders} />
          </div>
        )}
      </ClientOnly>
    </Section>
  );
};
